import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import type { Fast } from "@if-tracking/shared";

import { api } from "../api";
import { epochToInputValue, formatDuration, inputValueToEpoch } from "../format";

interface FastFormModalProps {
  fast: Fast | null;
  timezone: string;
  defaultTargetSeconds: number;
  onClose: () => void;
  onSaved: (message: string) => void | Promise<void>;
  onError: (message: string) => void;
}

export function FastFormModal({
  fast,
  timezone,
  defaultTargetSeconds,
  onClose,
  onSaved,
  onError
}: FastFormModalProps) {
  const isEdit = fast !== null;
  const isActiveEdit = fast?.end_ts === null;
  const now = useMemo(() => Date.now(), []);

  const [start, setStart] = useState(() =>
    epochToInputValue(fast?.start_ts ?? now - defaultTargetSeconds * 1000, timezone)
  );
  const [end, setEnd] = useState(() =>
    epochToInputValue(fast?.end_ts ?? now, timezone)
  );
  const [targetHours, setTargetHours] = useState(() =>
    ((fast?.target_seconds ?? defaultTargetSeconds) / 3600).toString()
  );
  const [note, setNote] = useState(fast?.note ?? "");
  const [busy, setBusy] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    modalRef.current?.querySelector<HTMLElement>("input")?.focus();
  }, []);

  function focusableElements(): HTMLElement[] {
    const nodes = modalRef.current?.querySelectorAll<HTMLElement>("input, button, select, textarea, [href]");
    return nodes ? Array.from(nodes).filter((node) => !node.hasAttribute("disabled")) : [];
  }

  function trapFocus(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Tab") return;
    const elements = focusableElements();
    if (elements.length === 0) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  const preview = useMemo(() => {
    const startTs = inputValueToEpoch(start, timezone);
    const endTs = isActiveEdit ? now : inputValueToEpoch(end, timezone);
    if (startTs === null || endTs === null) return null;
    const seconds = Math.round((endTs - startTs) / 1000);
    if (seconds <= 0) return { invalid: true as const };
    return {
      invalid: false as const,
      label: `${isActiveEdit ? "Elapsed" : "Duration"}: ${formatDuration(seconds)}`
    };
  }, [start, end, isActiveEdit, now, timezone]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const startTs = inputValueToEpoch(start, timezone);
    const endTs = isActiveEdit ? null : inputValueToEpoch(end, timezone);
    if (startTs === null || (!isActiveEdit && endTs === null)) {
      onError(isActiveEdit ? "Enter a valid start time." : "Enter a valid start and end time.");
      return;
    }
    if (isActiveEdit && startTs >= Date.now()) {
      onError("Start time must be before now.");
      return;
    }
    if (endTs !== null && endTs <= startTs) {
      onError("End time must be after the start time.");
      return;
    }
    const hours = Number(targetHours);
    if (!Number.isFinite(hours) || hours <= 0) {
      onError("Target hours must be a positive number.");
      return;
    }
    const targetSeconds = Math.round(hours * 3600);
    const trimmedNote = note.trim();

    setBusy(true);
    try {
      if (isEdit && fast) {
        await api.updateFast(fast.id, {
          start_ts: startTs,
          end_ts: endTs,
          target_seconds: targetSeconds,
          note: trimmedNote.length > 0 ? trimmedNote : null
        });
        await onSaved("Fast updated");
      } else if (endTs !== null) {
        await api.createFast({
          start_ts: startTs,
          end_ts: endTs,
          target_seconds: targetSeconds,
          note: trimmedNote.length > 0 ? trimmedNote : undefined
        });
        await onSaved("Fast added");
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={trapFocus}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fast-form-title"
      >
        <h2 id="fast-form-title">{isEdit ? "Edit fast" : "Add fast"}</h2>
        <form onSubmit={handleSubmit}>
          <label>
            Start
            <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} required />
          </label>
          {!isActiveEdit ? (
            <label>
              End
              <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} required />
            </label>
          ) : null}
          <label>
            Target (hours)
            <input
              type="number"
              min="0.25"
              step="0.25"
              value={targetHours}
              onChange={(e) => setTargetHours(e.target.value)}
              required
            />
          </label>
          <label>
            Note (optional)
            <input type="text" maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>

          <p className="form-preview muted">
            {preview === null
              ? "Times entered in " + timezone
              : preview.invalid
                ? isActiveEdit
                  ? "Start must be before now"
                  : "End must be after start"
                : `${preview.label} · ${timezone}`}
          </p>

          <div className="modal-actions">
            <button type="button" className="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={busy}>
              {busy ? "Saving…" : isEdit ? "Save changes" : "Add fast"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
